import React, { useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { Edit3, Save, X } from 'lucide-react';

const METHODS = [
  { value: 'none',          label: 'None (use per-item)' },
  { value: 'screen_print',  label: 'Screen Print' },
  { value: 'embroidery',    label: 'Embroidery' },
  { value: 'dtf',           label: 'DTF Transfer' },
  { value: 'dtg',           label: 'DTG' },
  { value: 'sublimation',   label: 'Sublimation' },
  { value: 'heat_transfer', label: 'Heat Transfer' },
  { value: 'vinyl',         label: 'Vinyl' },
];
const DEFAULT_LOCATIONS = ['Front', 'Back', 'Left Sleeve', 'Right Sleeve', 'Left Chest', 'Right Chest'];

const PrintSpecificationsEditor = ({ proposal, onSaved }) => {
  const details = proposal.print_details || {};
  const [editing, setEditing] = useState(false);
  const [method, setMethod] = useState(details.method || 'screen_print');
  const [locations, setLocations] = useState(Array.isArray(details.locations) ? details.locations : []);
  const [colorsPerLocation, setColorsPerLocation] = useState(details.colors_per_location || {});
  const [inkColorsText, setInkColorsText] = useState((details.ink_colors || []).join(', '));
  const [stitchCount, setStitchCount] = useState(details.stitch_count || '');
  const [cascade, setCascade] = useState(true);
  const [saving, setSaving] = useState(false);

  const toggleLoc = (loc) => setLocations((prev) => prev.includes(loc) ? prev.filter((l) => l !== loc) : [...prev, loc]);

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        method,
        locations,
        colors_per_location: colorsPerLocation,
        ink_colors: inkColorsText.split(',').map((s) => s.trim()).filter(Boolean),
        stitch_count: stitchCount ? parseInt(stitchCount) : null,
        cascade,
      };
      const r = await axios.patch(`/proposals/${proposal.id}/print-specs`, body);
      toast.success(cascade ? 'Print specs updated · cascaded to all items · repriced' : 'Print specs updated');
      onSaved?.(r.data.proposal);
      setEditing(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save print specs');
    } finally { setSaving(false); }
  };

  if (!editing) {
    return (
      <>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h4 style={{ fontSize: '16px', fontWeight: '600', margin: 0 }}>Print Specifications</h4>
          <Button size="sm" variant="outline" onClick={() => setEditing(true)} data-testid="edit-print-specs-btn">
            <Edit3 size={12} style={{ marginRight: 4 }} />Edit
          </Button>
        </div>
        <div style={{ background: '#f7fafc', padding: '16px', borderRadius: '8px' }}>
          <p style={{ marginBottom: '6px' }}><strong>Method:</strong> {(details.method || 'screen_print').replace('_', ' ').toUpperCase()}</p>
          <p style={{ marginBottom: '6px' }}><strong>Locations:</strong> {(details.locations || []).join(', ').toUpperCase() || 'N/A'}</p>
          {details.colors_per_location && Object.keys(details.colors_per_location).length > 0 && (
            <p style={{ marginBottom: '6px' }}>
              <strong>Colors:</strong> {Object.entries(details.colors_per_location).map(([loc, c]) => `${loc}: ${c}`).join(' · ')}
            </p>
          )}
          {(details.ink_colors || []).length > 0 && (
            <p style={{ marginBottom: '6px', fontSize: 13, color: '#475569' }}>
              <strong>Ink colors:</strong> {(details.ink_colors || []).join(', ')}
            </p>
          )}
          {details.stitch_count && <p style={{ marginBottom: 0, fontSize: 13, color: '#475569' }}><strong>Stitch count:</strong> {details.stitch_count.toLocaleString()}</p>}
        </div>
      </>
    );
  }

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h4 style={{ fontSize: '16px', fontWeight: '600', margin: 0 }}>Print Specifications · Editing</h4>
        <Button size="sm" variant="ghost" onClick={() => setEditing(false)} disabled={saving}><X size={12} style={{ marginRight: 4 }} />Cancel</Button>
      </div>
      <div style={{ background: '#f7fafc', padding: '16px', borderRadius: '8px', display: 'grid', gap: 14 }}>
        <div>
          <Label>Method (proposal-level default)</Label>
          <Select value={method} onValueChange={setMethod}>
            <SelectTrigger data-testid="ps-method"><SelectValue /></SelectTrigger>
            <SelectContent>
              {METHODS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label>Print locations</Label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
            {DEFAULT_LOCATIONS.map((loc) => {
              const active = locations.includes(loc);
              return (
                <button key={loc} type="button" onClick={() => toggleLoc(loc)} data-testid={`ps-loc-${loc}`}
                  style={{
                    padding: '5px 12px', borderRadius: 999, fontSize: 12, fontWeight: 700, cursor: 'pointer',
                    border: '1px solid', background: active ? '#0f172a' : '#fff',
                    color: active ? '#fff' : '#475569', borderColor: active ? '#0f172a' : '#cbd5e1',
                  }}>{loc}</button>
              );
            })}
          </div>
        </div>

        {method === 'screen_print' && locations.length > 0 && (
          <div>
            <Label>Colors per location (screen print)</Label>
            <div style={{ display: 'grid', gap: 6, marginTop: 6 }}>
              {locations.map((loc) => (
                <div key={loc} style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: 8, alignItems: 'center' }}>
                  <span style={{ fontSize: 13, color: '#475569' }}>{loc}</span>
                  <Input type="number" min={1} max={8} value={colorsPerLocation[loc] || 1}
                    onChange={(e) => setColorsPerLocation({ ...colorsPerLocation, [loc]: parseInt(e.target.value) || 1 })}
                    data-testid={`ps-colors-${loc}`} />
                </div>
              ))}
            </div>
          </div>
        )}

        {method === 'embroidery' && (
          <div>
            <Label>Stitch count</Label>
            <Input type="number" min={1000} step={500} value={stitchCount} onChange={(e) => setStitchCount(e.target.value)} data-testid="ps-stitch" />
          </div>
        )}

        <div>
          <Label>Ink / thread colors (comma-separated)</Label>
          <Input value={inkColorsText} onChange={(e) => setInkColorsText(e.target.value)} placeholder="e.g. Gold 1672, Navy 1743, White" data-testid="ps-ink-colors" />
        </div>

        <label style={{
          display: 'flex', alignItems: 'center', gap: 8, fontSize: 14,
          color: cascade ? '#92400e' : '#475569', cursor: 'pointer',
          padding: '10px 12px',
          background: cascade ? '#fef3c7' : '#f1f5f9',
          border: `1px solid ${cascade ? '#f59e0b' : '#cbd5e1'}`,
          borderRadius: 8,
          fontWeight: cascade ? 600 : 400,
        }}>
          <input type="checkbox" checked={cascade} onChange={(e) => setCascade(e.target.checked)} data-testid="ps-cascade" />
          <span>{cascade
            ? 'Apply these settings to every item (each line item will be re-priced from the matrix)'
            : 'Update proposal-level defaults only — does NOT change existing items'}</span>
        </label>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button onClick={save} disabled={saving} data-testid="ps-save-btn" style={{ background: '#0f172a', color: '#fff' }}>
            <Save size={14} style={{ marginRight: 4 }} />{saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>
    </>
  );
};

export default PrintSpecificationsEditor;
